import React from 'react';
import { Dialog, DialogContent } from '@mui/material';
import { PDFViewer, PhotoViewer } from 'components';
import ReactPlayer from 'react-player';
import makeStyles from '@mui/styles/makeStyles';
import { useShouldModalBeFullscreen } from 'utils/media-query';
import { File } from '../../index';
import { images, pdfs, otherDocuments, videos, audios } from '../../utils';
import { ModalClose } from '../../../../components';
import { Stack } from '../../../../components/v2/Stack/Stack';
import Button from '../../../../components/v2/Button/Button';
import Icon from '../../../../components/v2/Icon/Icon';
import { downloadOriginal } from '../../service/api';
import { useNavigate } from 'react-router-dom';
import { useCurrentDir } from '../../../ui/hooks';
import { useSingleFile } from '../../hooks';
import useFileQuery from '../../hooks/use-file-query';

const useStyles = makeStyles(() => ({
  content: {
    display: 'flex',
    flexDirection: 'row',
    padding: '0 !important',
    height: '95vh',
    alignItems: 'center',
  },
  videoContent: {
    display: 'flex',
    minHeight: '550px',
    flexDirection: 'row',
    padding: '0 !important',
    justifyContent: 'center',
    alignItems: 'center',
  },
  paper: {
    overflow: 'unset',
    borderRadius: 0,
  },
}));

interface PreviewProps {
  onClose(): void;
  file?: Pick<File, 'id' | 'type' | 'originalUrl' | 'previewUrl' | 'directory'>;
  src: string;
  loader?: any;
}

export const DocumentPreviewModal: React.FC<PreviewProps> = ({
  onClose,
  src,
  loader = null,
}) => {
  const classes = useStyles();
  const fullScreen = useShouldModalBeFullscreen();

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="lg"
      PaperProps={{ className: classes.paper }}
    >
      <DialogContent className={classes.content}>
        <ModalClose onClose={onClose} onOutside />
        <PDFViewer url={src} onClose={onClose} loader={loader} />
      </DialogContent>
    </Dialog>
  );
};

export const VideoPreviewModal: React.FC<PreviewProps> = ({ onClose, src }) => {
  const classes = useStyles();
  const fullScreen = useShouldModalBeFullscreen();

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="md"
      PaperProps={{ className: classes.paper }}
    >
      <DialogContent className={classes.videoContent}>
        <ModalClose onClose={onClose} onOutside />
        <ReactPlayer url={src} playing controls width="900px" height="540px" />
      </DialogContent>
    </Dialog>
  );
};

export const OfficeDocumentPreviewModal: React.FC<PreviewProps> = ({
  onClose,
  src,
  file,
}) => {
  const classes = useStyles();
  const fullScreen = useShouldModalBeFullscreen();
  const navigate = useNavigate();
  const fileQuery = useFileQuery(file?.id);

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="lg"
      PaperProps={{ className: classes.paper }}
    >
      <ModalClose onClose={onClose} onOutside />
      <div style={{ position: 'absolute', right: '0', top: '-32px' }}>
        <Stack direction="row" align="center" gap={1}>
          <Button
            variant="icon"
            style={{ color: '#fff' }}
            onClick={() => {
              if (!file) {
                console.log('File missing.', file);
                return;
              }

              void downloadOriginal({ id: file.id });
            }}
          >
            <Icon name="fa-regular fa-download" />
          </Button>
          {fileQuery.data?.directory && (
            <Button
              style={{ color: '#fff' }}
              variant="icon"
              onClick={() => {
                if (fileQuery.data?.directory) {
                  navigate(`/v2/directory/${fileQuery.data?.directory.id}`);
                  onClose();
                }
              }}
            >
              <Icon name="fa-regular fa-folder" />
            </Button>
          )}
        </Stack>
      </div>
      <DialogContent
        className={classes.content}
        style={{ overflow: 'hidden', alignItems: 'flex-start' }}
      >
        <iframe
          title="MS Document preview"
          style={{
            width: '100%',
            height: 'calc(100% + 23px)',
          }}
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
            src,
          )}`}
          frameBorder="0"
        />
      </DialogContent>
    </Dialog>
  );
};

interface Props {
  file: Pick<File, 'id' | 'type' | 'originalUrl' | 'previewUrl'>;
  onClose(): void;
}

const FilePreviewModal: React.FC<Props> = ({ file, onClose }) => {
  if (!file.previewUrl) {
    return null;
  }

  if (images.includes(file.type)) {
    return <PhotoViewer onClose={onClose} src={file.previewUrl} />;
  }

  if (pdfs.includes(file.type)) {
    return (
      <DocumentPreviewModal
        onClose={onClose}
        src={file.previewUrl}
        file={file}
      />
    );
  }

  if (videos.includes(file.type) || audios.includes(file.type)) {
    if (!file.originalUrl) {
      return null;
    }

    return <VideoPreviewModal onClose={onClose} src={file.originalUrl} />;
  }

  if (otherDocuments.includes(file.type)) {
    if (!file.originalUrl) {
      return null;
    }

    return (
      <OfficeDocumentPreviewModal
        onClose={onClose}
        file={file}
        src={file.originalUrl}
      />
    );
  }

  return null;
};

export default FilePreviewModal;
